/**
 * Suite colour swatch picker for metaobject suite pages and list cards.
 */

/**
 * @param {string | undefined} raw
 * @returns {string}
 */
function decodePreviewUrl(raw) {
  return raw?.trim().replace(/&amp;/g, '&') ?? '';
}

/**
 * @param {HTMLElement} control
 * @param {HTMLElement} picker
 * @returns {string}
 */
function resolvePreviewImage(control, picker) {
  let previewImage = decodePreviewUrl(control.dataset.suitePreviewImage);

  if (!previewImage) {
    const cardIndexRaw = control.dataset.suiteCardImageIndex;
    const galleryImages = picker.dataset.cardGalleryImages?.trim();

    if (galleryImages && cardIndexRaw !== undefined && cardIndexRaw !== '') {
      const index = parseInt(cardIndexRaw, 10);
      if (!Number.isNaN(index)) {
        const urls = galleryImages.split('|||').filter(Boolean);
        previewImage = decodePreviewUrl(urls[index]);
      }
    }
  }

  return previewImage;
}

/**
 * @param {HTMLElement} picker
 * @param {HTMLElement} control
 */
function applySuiteCardSwatch(picker, control) {
  const previewImage = resolvePreviewImage(control, picker);
  if (!previewImage) return;

  const card = picker.closest('.suite-card');
  if (!card) return;

  const cardImage =
    card.querySelector('[data-suite-card-image]') ??
    card.querySelector('.collection-card__image img.image-block__image') ??
    card.querySelector('.collection-card__image img');

  if (!(cardImage instanceof HTMLImageElement)) return;

  cardImage.src = previewImage;
  cardImage.removeAttribute('srcset');
  cardImage.removeAttribute('sizes');
  cardImage.loading = 'eager';

  const cardLink = card.querySelector('a.collection-card__link');
  const variantUrl = control.dataset.suiteVariantUrl?.trim();
  if (variantUrl && cardLink instanceof HTMLAnchorElement) {
    cardLink.href = variantUrl;
  }

  picker.querySelectorAll('.suite-colour-picker__swatch').forEach((element) => {
    element.classList.remove('suite-colour-picker__swatch--current');
  });
  control.classList.add('suite-colour-picker__swatch--current');
}

/**
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function findCardSwatchFromEvent(event) {
  if (!(event.target instanceof Element)) return null;

  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

  for (const node of path) {
    if (!(node instanceof Element)) continue;

    const candidate = node.closest('.suite-colour-picker__swatch');
    if (candidate instanceof HTMLElement) {
      return candidate;
    }
  }

  const control = event.target.closest('.suite-colour-picker__swatch');
  return control instanceof HTMLElement ? control : null;
}

/**
 * @param {Event} event
 * @param {HTMLElement} picker
 * @returns {boolean}
 */
function eventTargetsPicker(event, picker) {
  if (picker.contains(event.target instanceof Node ? event.target : null)) {
    return true;
  }

  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  return path.includes(picker);
}

/**
 * @param {Event} event
 */
function handleDocumentCardSwatchClick(event) {
  const control = findCardSwatchFromEvent(event);
  if (!control) return;

  const picker = control.closest('suite-colour-picker[data-card-context]');
  if (!(picker instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();

  applySuiteCardSwatch(picker, control);
}

/**
 * @param {Event} event
 */
function handleDocumentCardImageClick(event) {
  if (!(event.target instanceof Element)) return;
  if (event.target.closest('.suite-colour-picker__swatch')) return;

  const imageArea = event.target.closest('.suite-card .collection-card__image');
  if (!imageArea) return;

  const card = imageArea.closest('.suite-card');
  const href = card?.querySelector('a.collection-card__link')?.getAttribute('href');
  if (!href) return;

  window.location.assign(href);
}

function bindSuiteCardSwatchEvents() {
  if (window.__suiteCardSwatchEvents) return;

  window.__suiteCardSwatchEvents = true;
  document.addEventListener('click', handleDocumentCardSwatchClick, true);
  document.addEventListener('click', handleDocumentCardImageClick);
}

bindSuiteCardSwatchEvents();

class SuiteColourPicker extends HTMLElement {
  connectedCallback() {
    const isCardContext = this.hasAttribute('data-card-context');
    const overflowList = this.querySelector('overflow-list');

    if (overflowList) {
      overflowList.removeAttribute('defer');
      this.#expandOverflowList(overflowList);
    }

    if (isCardContext) {
      this.addEventListener('click', this.#handleCardClick, true);
    } else {
      this.addEventListener('click', this.#handleGalleryClick, true);
      this.#ensureGalleryBindings();
    }
  }

  /**
   * @param {Element | null} overflowList
   */
  #expandOverflowList(overflowList) {
    const tryShowAll = (attempts = 0) => {
      if (
        overflowList &&
        overflowList.shadowRoot &&
        'showAll' in overflowList &&
        typeof overflowList.showAll === 'function'
      ) {
        overflowList.showAll();
        return;
      }

      if (attempts < 20) {
        requestAnimationFrame(() => tryShowAll(attempts + 1));
      }
    };

    tryShowAll();
  }

  async #ensureGalleryBindings() {
    await customElements.whenDefined('suite-media-gallery');

    const gallery = this.#findGallery();

    if (gallery && typeof gallery.bindSwatches === 'function') {
      gallery.bindSwatches();
    }
  }

  #findGallery() {
    const suiteId = this.dataset.currentSuiteId;

    return (
      (suiteId
        ? document.querySelector(`suite-media-gallery[data-suite-id="${suiteId}"]`)
        : null) ??
      this.closest('.shopify-section')?.querySelector('suite-media-gallery') ??
      document.querySelector('suite-media-gallery')
    );
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #findSwatchFromEvent(event) {
    const control = findCardSwatchFromEvent(event);
    if (!(control instanceof HTMLElement)) return null;
    if (!eventTargetsPicker(event, this)) return null;

    return control;
  }

  /**
   * @param {Event} event
   */
  #handleCardClick = (event) => {
    const control = this.#findSwatchFromEvent(event);
    if (!control) return;

    event.preventDefault();
    event.stopPropagation();

    applySuiteCardSwatch(this, control);
  };

  /**
   * @param {Event} event
   */
  #handleGalleryClick = async (event) => {
    const control = this.#findSwatchFromEvent(event);
    if (!control) return;

    event.preventDefault();
    event.stopPropagation();

    await customElements.whenDefined('suite-media-gallery');

    const gallery = this.#findGallery();
    if (!gallery || typeof gallery.selectForSwatch !== 'function') return;

    gallery.selectForSwatch(control);

    this.querySelectorAll('.suite-colour-picker__swatch').forEach((element) => {
      element.classList.remove('suite-colour-picker__swatch--current');
    });
    control.classList.add('suite-colour-picker__swatch--current');
  };
}

if (!customElements.get('suite-colour-picker')) {
  customElements.define('suite-colour-picker', SuiteColourPicker);
}

export { SuiteColourPicker, applySuiteCardSwatch, bindSuiteCardSwatchEvents };
